import * as Phaser from 'phaser';
import { NUTRIENTS, type NutrientKey } from '@/config/nutrients';

export const TEXTURE_KEYS = {
  elephant: 'tex_elephant',
  monster: 'tex_monster',
  shield: 'tex_shield',
  background: 'tex_bg',
  nutrient: (key: NutrientKey) => `tex_nutrient_${key}`,
} as const;

export function generateAllTextures(scene: Phaser.Scene): void {
  generateElephantTexture(scene);
  generateMonsterTexture(scene);
  generateShieldTexture(scene);
  generateBackgroundTexture(scene);
  for (const key of Object.keys(NUTRIENTS) as NutrientKey[]) {
    generateNutrientTexture(scene, key);
  }
}

function generateElephantTexture(scene: Phaser.Scene): void {
  const size = 96;
  const cx = size / 2;
  const cy = size / 2;
  const g = scene.make.graphics({}, false);
  g.fillStyle(0xb8b8b8, 1);
  g.fillEllipse(cx, cy + 8, 70, 56);
  g.fillStyle(0xb8b8b8, 1);
  g.fillCircle(cx, cy - 18, 22);
  g.fillStyle(0x9a9a9a, 1);
  g.fillEllipse(cx - 22, cy - 16, 14, 18);
  g.fillEllipse(cx + 22, cy - 16, 14, 18);
  g.fillStyle(0xb8b8b8, 1);
  g.fillRoundedRect(cx - 5, cy - 8, 10, 26, 5);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx - 7, cy - 20, 4);
  g.fillCircle(cx + 7, cy - 20, 4);
  g.fillStyle(0x111111, 1);
  g.fillCircle(cx - 7, cy - 20, 2);
  g.fillCircle(cx + 7, cy - 20, 2);
  g.generateTexture(TEXTURE_KEYS.elephant, size, size);
  g.destroy();
}

function generateMonsterTexture(scene: Phaser.Scene): void {
  const size = 60;
  const cx = size / 2;
  const cy = size / 2;
  const g = scene.make.graphics({}, false);
  g.fillStyle(0xd83232, 1);
  g.fillPoints(makeStarPoints(cx, cy, 10, 22, 16), true);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx - 6, cy - 2, 3);
  g.fillCircle(cx + 6, cy - 2, 3);
  g.fillStyle(0x111111, 1);
  g.fillCircle(cx - 6, cy - 2, 1.5);
  g.fillCircle(cx + 6, cy - 2, 1.5);
  g.lineStyle(2, 0x111111, 1);
  g.beginPath();
  g.moveTo(cx - 6, cy + 8);
  g.lineTo(cx, cy + 6);
  g.lineTo(cx + 6, cy + 8);
  g.strokePath();
  g.generateTexture(TEXTURE_KEYS.monster, size, size);
  g.destroy();
}

function generateShieldTexture(scene: Phaser.Scene): void {
  const size = 80;
  const cx = size / 2;
  const cy = size / 2;
  const g = scene.make.graphics({}, false);
  g.lineStyle(6, 0xffd84a, 1);
  g.strokeCircle(cx, cy, 32);
  g.lineStyle(2, 0xfff5a0, 0.9);
  g.strokeCircle(cx, cy, 28);
  g.fillStyle(0xffd84a, 0.18);
  g.fillCircle(cx, cy, 30);
  g.fillStyle(0xffffff, 1);
  g.fillPoints(makeStarPoints(cx, cy, 5, 12, 5), true);
  g.generateTexture(TEXTURE_KEYS.shield, size, size);
  g.destroy();
}

function generateNutrientTexture(scene: Phaser.Scene, key: NutrientKey): void {
  const def = NUTRIENTS[key];
  const size = 40;
  const cx = size / 2;
  const cy = size / 2;
  const isDielac = def.tooltip !== undefined;
  const g = scene.make.graphics({}, false);
  if (isDielac) {
    g.fillStyle(0xffffff, 1);
    g.fillCircle(cx, cy, 18);
    g.fillStyle(def.color, 1);
    g.fillCircle(cx, cy, 15);
    g.lineStyle(1.5, 0xffffff, 0.9);
    g.strokeCircle(cx, cy, 11);
  } else {
    g.fillStyle(def.color, 1);
    g.fillCircle(cx, cy, 16);
    g.lineStyle(2, 0x6b4d2a, 0.7);
    g.strokeCircle(cx, cy, 16);
  }
  g.generateTexture(TEXTURE_KEYS.nutrient(key), size, size);
  g.destroy();
}

function makeStarPoints(
  cx: number,
  cy: number,
  spikes: number,
  outerR: number,
  innerR: number,
): Phaser.Math.Vector2[] {
  const points: Phaser.Math.Vector2[] = [];
  for (let i = 0; i < spikes * 2; i++) {
    const angle = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    const radius = i % 2 === 0 ? outerR : innerR;
    points.push(
      new Phaser.Math.Vector2(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius),
    );
  }
  return points;
}

function generateBackgroundTexture(scene: Phaser.Scene): void {
  const w = 360;
  const h = 64;
  const g = scene.make.graphics({}, false);
  g.fillStyle(0x9adfff, 1);
  g.fillRect(0, 0, w, h);
  g.fillStyle(0x7fc8f0, 0.4);
  for (let i = 0; i < 6; i++) {
    const x = (i * 71 + 13) % w;
    const y = (i * 41 + 9) % h;
    g.fillCircle(x, y, 8);
  }
  g.generateTexture(TEXTURE_KEYS.background, w, h);
  g.destroy();
}
